import React from 'react';
import { Facebook, Linkedin, Twitter, Instagram, Youtube, Rss, Mail, ArrowUp } from 'lucide-react';
import { t, getCurrentLanguage } from '../utils/translations';
import { useState, useEffect } from 'react';
import i18n from '../i18n';

// Icône WhatsApp SVG (non disponible dans lucide-react)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => setCurrentLanguage(getCurrentLanguage());
    i18n.on('languageChanged', handleLanguageChange);
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  const sectors = [
    t('footer.sector1'),
    t('footer.sector2'),
    t('footer.sector3'),
    t('footer.sector4'),
    t('footer.sector5')
  ];

  const topics = currentLanguage === 'en'
    ? ['Environment and Climate', 'Living Conditions', 'Sustainable Development']
    : ['Environnement et Climat', 'Cadre de Vie', 'Développement Durable'];

  const microsites = [
    { title: currentLanguage === 'en' ? 'Ongoing Projects'  : 'Projets en Cours',    href: null },
    { title: currentLanguage === 'en' ? 'Annual Reports'    : 'Rapports Annuels',    href: "/Rapport_d'activiter_AHB.pdf" },
    { title: currentLanguage === 'en' ? 'Internal Rules'    : 'Règlement Intérieur', href: '/REGLEMENT INTERIEUR_AHB.pdf' },
    { title: currentLanguage === 'en' ? 'Accreditation'     : 'Agrément',            href: null },
  ];

  const resources = [
    t('footer.resource1'),
    t('footer.resource2'),
    t('footer.resource3'),
    t('footer.resource4')
  ];

  const legalLinks = [
    t('footer.legal1'),
    t('footer.legal2'),
    t('footer.legal3'),
    t('footer.legal4'),
    t('footer.legal5'),
    t('footer.legal6'),
    t('footer.legal7')
  ];

  const socialIcons = [
    { icon: Facebook,     href: 'https://web.facebook.com/ahbeninweb/',                              name: 'facebook'  },
    { icon: Linkedin,     href: 'https://www.linkedin.com/company/ahbeninweb/?viewAsMember=true',    name: 'linkedin'  },
    { icon: WhatsAppIcon, href: 'https://whatsapp.com/channel/0029Vb3ZDup002TAZKOstW2U',            name: 'whatsapp'  },
    { icon: Twitter,      href: '#',                                                                  name: 'twitter'   },
    { icon: Instagram,    href: '#',                                                                  name: 'instagram' },
    { icon: Youtube,      href: '#',                                                                  name: 'youtube'   },
    { icon: Mail,         href: 'mailto:contact@ahbenin.org',                                        name: 'mail'      },
  ];

  return (
    <footer className="bg-[#089556] text-white">
      <div className="max-w-7xl mx-auto px-4 pt-12 pb-6">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Sectors */}
          <div>
            <h3 className="bg-[#FAFBE5] text-[#089556] p-3 font-semibold mb-4">
              {t('footer.sectorsTitle')}
            </h3>
            <ul className="space-y-2">
              {sectors.map((sector) => (
                <li key={sector} className="hover:text-[#07D1EB]">
                  <button className="flex items-center text-left w-full">
                    <span className="mr-2">-</span>
                    {sector}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Topics */}
          <div>
            <h3 className="bg-[#FAFBE5] text-[#089556] p-3 font-semibold mb-4">
              {currentLanguage === 'en' ? 'Our Topics' : 'Nos Thématiques'}
            </h3>
            <ul className="space-y-2">
              {topics.map((topic) => (
                <li key={topic} className="hover:text-[#07D1EB]">
                  <button className="flex items-center text-left w-full">
                    <span className="mr-2">-</span>
                    {topic}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Microsites */}
          <div>
            <h3 className="bg-[#FAFBE5] text-[#089556] p-3 font-semibold mb-4">
              {currentLanguage === 'en' ? 'Our Microsites' : 'Nos Microsites'}
            </h3>
            <ul className="space-y-2">
              {microsites.map((site) => (
                <li key={site.title} className="hover:text-[#07D1EB]">
                  {site.href ? (
                    <a
                      href={site.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-left w-full"
                    >
                      <span className="mr-2">-</span>
                      {site.title}
                    </a>
                  ) : (
                    <button className="flex items-center text-left w-full">
                      <span className="mr-2">-</span>
                      {site.title}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Connect & Resources */}
          <div className="space-y-8">
            {/* Connect with us */}
            <div>
              <h3 className="bg-[#FAFBE5] text-[#089556] p-3 font-semibold mb-4">
                {t('footer.connectTitle')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {socialIcons.map(({ icon: Icon, href, color, name }) => (
                  <a
                    key={name}
                    href={href}
                    target={href !== '#' ? '_blank' : undefined}
                    rel={href !== '#' ? 'noopener noreferrer' : undefined}
                    className="bg-[#07D1EB] p-2 rounded-md hover:bg-[#089556] transition-all duration-300"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div>
              <h3 className="bg-[#FAFBE5] text-[#089556] p-3 font-semibold mb-4">
                {t('footer.resourcesTitle')}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {resources.map((resource) => (
                  <div key={resource} className="hover:text-[#07D1EB]">
                    <button className="flex items-center text-left w-full">
                      <span className="mr-2">-</span>
                      {resource}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              {legalLinks.map((link) => (
                <button
                  key={link}
                  className="hover:text-gray-200 whitespace-nowrap"
                >
                  {link}
                </button>
              ))}
            </div>

            {/* Copyright & Registration Info */}
            <div className="text-sm text-center md:text-right">
              <div className="whitespace-nowrap mb-1">
                © {new Date().getFullYear()} Action-Humanitaire Bénin. {currentLanguage === 'en' ? 'All rights reserved.' : 'Tous droits réservés.'}
              </div>
              <div className="text-xs opacity-90">
                {currentLanguage === 'en' ? 'AHB Register' : 'Registre de AHB'}: No. 177/MISP/DC/SGM/DAIC/SACC/SA
              </div>
              <div className="text-xs opacity-90">
                IFU: 6202565151962
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-black/50 hover:bg-black/70 p-2 rounded-full transition-colors"
        aria-label={t('footer.scrollToTop')}
      >
        <ArrowUp className="h-6 w-6" />
      </button>
    </footer>
  );
};

export default Footer;